import React, { useContext, useState,useEffect } from "react";
import { ENTITY_LABELS } from "../../../utils/labelMapping";
import { DiscoveryContext } from "../discovery-context";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  LinearProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import MUIButton from "../../../Component/UI/MUIButton/MUIButton";
import classes from "./SelectedComponent.module.css";
import DownloadIcon from "@mui/icons-material/Download"; // example icon
import CancelIcon from "@mui/icons-material/Cancel";
import { useRef } from "react";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { toast } from "react-toastify";
import { Checkbox, FormControlLabel } from "@mui/material";
import { tab, tabName } from "../BrandScore/BrandScore";
import { LLM_OPTIONS, OVERVIEW_LLM_LABELS, getConversationTables } from "../../../utils/constant";
import { generateCombination } from "../../../utils/helpers";
import { SettingsOutlined } from "@mui/icons-material";
import PPTReportConfigModal from './PPTReportConfigModal';
import { generateComponentKeys,getDashboardDependencies } from "./useDashboardComponents";
import { validatePersonaSelection } from "./PersonaScopeResolver";

const SelectedComponent = ({
  generateReport,
  loading,
  auditJobStatus,
  auditPercent,
}) => {
  const discoveryCtx = useContext(DiscoveryContext);
  const discoveryType = discoveryCtx.type || "brand";
  let clickSelectAll = discoveryCtx.clickSelectAll;
  const [showPPTConfigModal, setShowPPTConfigModal] = useState(false);
  let { llm = [], category = [], markets = [], market, primary_llm } = discoveryCtx;
  const SelectedSection = Object.values(discoveryCtx.state.selectedSection);
  const competitorsCategoryWise = discoveryCtx?.competitors ?? {};
  const entity = discoveryCtx.entity;
  const personaSetting = discoveryCtx?.state?.personaSettings;
  const selectAll = discoveryCtx?.state?.selectAll ?? false;
  const containerRef = useRef(null);
  const extractPersonaTitles = (personas = []) =>
    personas.map(p => (typeof p === "string" ? p : p?.title)).filter(Boolean);
  const [lastSubmittedConfig, setLastSubmittedConfig] = useState(null);
  const [modalGeneratedKeys, setModalGeneratedKeys] = useState([]);
  const [modalSelections, setModalSelections] = useState({
    categories: [],
    llms: [],
    markets: [],
    personas: [],
    dashboards: [],
  });
  const [previousDashboardIds, setPreviousDashboardIds] = useState([]);

  const persona = extractPersonaTitles(discoveryCtx?.persona);
  const primaryPersona = persona[0];
  const barVisible =
    auditJobStatus === "starting" ||
    auditJobStatus === "pending" ||
    auditJobStatus === "running";

  const BASE_HEIGHT = 200; // current height
  const BAR_HEIGHT = 60; // room for bar + label

  const scroll = (direction) => {
    if (containerRef.current) {
      containerRef.current.scrollBy({
        left: direction === "left" ? -250 : 250, // adjust scroll distance
        behavior: "smooth",
      });
    }
  };

  //Compare two configs for equality
  const areConfigsEqual = (config1, config2) => {
    if (!config1 || !config2) return false;

    const arraysEqual = (arr1, arr2) => {
      if (arr1.length !== arr2.length) return false;
      const sorted1 = [...arr1].sort();
      const sorted2 = [...arr2].sort();
      return sorted1.every((val, idx) => val === sorted2[idx]);
    };

    return (
      arraysEqual(config1.categories, config2.categories) &&
      arraysEqual(config1.llms, config2.llms) &&
      arraysEqual(config1.markets, config2.markets) &&
      arraysEqual(config1.personas, config2.personas) &&
      arraysEqual(config1.dashboards, config2.dashboards)
    );
  };

  const handleDelete = (uniqueKey) => {
    discoveryCtx.clickComponentCheckbox({ uniqueKey, checked: true });
  };

  const reserModalConfig = () => {
    setModalSelections({
      categories: [],
      llms: [],
      markets: [],
      personas: [],
      dashboards: [],
    });
    setLastSubmittedConfig(null);
    setModalGeneratedKeys([]);
  }

  const selectReportSection = () => {
    discoveryCtx.cancelSelection();
    reserModalConfig();
  };

  const handleDownload = () => {
    if (!SelectedSection.length) {
      toast.warn("Select at least one combination to export report.");
    } else {
      generateReport("brand-audit-checkbox");
    }
  };

  // Handler for Report Configuration button
  const handleReportConfig = () => {
    setShowPPTConfigModal(true);
  };

  const handleCloseModal = () => {
    setShowPPTConfigModal(false);
  };

  useEffect(() => {
    if (!showPPTConfigModal) return;
    
    const dashboardDeps = getDashboardDependencies(discoveryCtx);
    if (dashboardDeps?.isDashboardSelection) {
      const dashboardConfig = {
        categories: dashboardDeps.categories,
        llms: dashboardDeps.llms,
        markets: dashboardDeps.markets,
        personas: dashboardDeps.personas,
        dashboards: dashboardDeps.dashboards,
      };
      if (!areConfigsEqual(dashboardConfig, lastSubmittedConfig)) {
        setLastSubmittedConfig(dashboardConfig);
        setModalSelections(dashboardConfig);
      }
      
      setPreviousDashboardIds(dashboardDeps.dashboardIds);
    } else {
      // No dashboard-level selection detected
      if (previousDashboardIds.length > 0) {
        setPreviousDashboardIds([]);
      }
    }
  }, [showPPTConfigModal, discoveryCtx.state.selectedSection]);

  useEffect(() => {
    const dashboardDeps = getDashboardDependencies(discoveryCtx);
    const currentDashboardIds = dashboardDeps?.isDashboardSelection 
      ? dashboardDeps.dashboardIds 
      : [];
    
    const removedDashboards = previousDashboardIds.filter(
      prevId => !currentDashboardIds.includes(prevId)
    );
    
    if (removedDashboards.length > 0) {
      
      if (currentDashboardIds.length > 0 && dashboardDeps?.isDashboardSelection) {
        const updatedConfig = {
          categories: dashboardDeps.categories,
          llms: dashboardDeps.llms,
          markets: dashboardDeps.markets,
          personas: dashboardDeps.personas,
          dashboards: dashboardDeps.dashboards,
        };
        
        setLastSubmittedConfig(updatedConfig);
        setModalSelections(updatedConfig);
        setPreviousDashboardIds(currentDashboardIds);
      } else {
        setPreviousDashboardIds([]);
      }
    } else if (currentDashboardIds.length > previousDashboardIds.length) {
      setPreviousDashboardIds(currentDashboardIds);
    }
  }, [discoveryCtx.state.selectedSection]);

  const handleGeneratePPTCombinations = (config) => {
    const { categories, llms, markets, personas, dashboards } = config;
  // Check if config is identical to last submission
    if (areConfigsEqual(config, lastSubmittedConfig)) {
      toast.warn("Selected combination already exists. Please change selection to generate a new combination.");
      return;
    }

  // ADD VALIDATION: Check if selected combination is valid based on persona settings
    const primaryCategory = category[0];
    
    const validation = validatePersonaSelection({
      selectedCategories: categories,
      selectedMarkets: markets,
      selectedPersonas: personas,
      primaryCategory,
      primaryMarket: market,
      primaryPersona,
      personaSetting,
    });

    if (!validation.isValid) {
      toast.warn(validation.invalidReason || "Selected combination does not exist");
    return; // Do NOT close modal, do NOT call generateComponentKeys
    }

  //  Validation passed - proceed with generation
    if (selectAll) {
      discoveryCtx.clickSelectAll({}); 
    }
    discoveryCtx.toggleDashboardSelection({clear:true})

    const newGeneratedKeys = [];

    dashboards.forEach((dashboardId) => {
      const isCategoryTab = !['nev_brandoverview', 'nev_competitiveoverview', 'nev_marketoverview', 'nev_personaoverview'].includes(dashboardId);
      const categoryForTab = isCategoryTab ? tabName[dashboardId] : null;

      const componentKeys = generateComponentKeys({
        dashboardTab: dashboardId,
        category: categoryForTab,
        allCategories: categories,
        allLLMs: llms,
        allMarkets: markets,
        allPersonas: personas,
        discoveryCtx,
      });

      componentKeys.forEach(comp => {
        newGeneratedKeys.push(comp.uniqueKey);
      });

      discoveryCtx.toggleDashboardSelection({componentKeys});
    });

    setModalGeneratedKeys(newGeneratedKeys);
    setModalSelections(config);
    setLastSubmittedConfig(config);

    setShowPPTConfigModal(false);
    toast.success('PPT Report combinations generated successfully!');
  };
 
  const getDashboardList = () => {
    const dashboards = [
      { id: 'nev_brandoverview', name: `${discoveryType === 'brand' ? ENTITY_LABELS.brand : ENTITY_LABELS.product} Overview` },
      { id: 'nev_competitiveoverview', name: 'Competitive Overview' },
    ];

    // Add Market Overview if multiple markets
    if (markets.length > 1) {
      dashboards.push({ id: 'nev_marketoverview', name: 'Market Overview' });
    }

    // Add Persona Overview if multiple personas
    if (persona.length > 1) {
      dashboards.push({ id: 'nev_personaoverview', name: 'Persona Overview' });
    }

    category.forEach((cat) => {
      const categoryKey = cat.replace(/\s+/g, "_");
      const tabId = `nav_${categoryKey}`;
      dashboards.push({ id: tabId, name: cat });
    });

    return dashboards;
  };
  const getPreselectedValues = () => {
  // Get preselected values for Select All scenario
    if (selectAll) {
      return {
        categories: category,
        llms: llm,
        markets: markets,
        personas: persona,
        dashboards: getDashboardList().map(d => d.id),
      };
    }
  //Dashboard-Level Selection (Single or Multiple)
    const dashboardDeps = getDashboardDependencies(discoveryCtx);
    if (dashboardDeps?.isDashboardSelection) {
      return {
        categories: dashboardDeps.categories,
        llms: dashboardDeps.llms,
        markets: dashboardDeps.markets,
        personas: dashboardDeps.personas,
        dashboards: dashboardDeps.dashboards,
      };
    }

  //Previous Modal Selections
    const hasSelections = Object.values(modalSelections).some(arr => arr.length > 0);
    if (hasSelections) {
      return modalSelections;
    }

    return null;
  };

  const handleCheck = () => {
    if (!selectAll) {
      let combination = CreateCombination({
        llm,
        category,
        market: markets,
        persona,
        primaryMarket: market,
        primaryLLM: primary_llm,
        primaryPersona,
        discoveryType,
        competitorsCategoryWise,
        entity,
        personaSetting
      });
      // Map any static titles in the generated combination to propensity wording when needed
      try {
        const mapTitles = (obj) => {
          if (!obj || typeof obj !== "object") return;
          if (Array.isArray(obj)) return obj.forEach(mapTitles);
          Object.keys(obj).forEach((k) => {
            if (k === "title" && typeof obj[k] === "string") {
              obj[k] = discoveryCtx?.mapMetricTitle?.(obj[k]) ?? obj[k];
            } else {
              mapTitles(obj[k]);
            }
          });
        };
        mapTitles(combination);
      } catch (err) {
        console.warn("Failed to map combination titles", err);
      }
      clickSelectAll(combination);
      const selectAllConfig = {
        categories: category,
        llms: llm,
        markets: markets,
        personas: persona,
        dashboards: getDashboardList().map(d => d.id),
      };
      setLastSubmittedConfig(selectAllConfig);
      setModalSelections(selectAllConfig);
    } else {
      clickSelectAll({});
      reserModalConfig();
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 20,
        right: 20,
        // height: "160px",
        height: `${BASE_HEIGHT + (barVisible ? BAR_HEIGHT : 0)}px`,
        boxShadow: 3,
        borderRadius: 3,
        p: 1,
        display: "flex",
        alignItems: "center",
        zIndex: 50,
        flexDirection: "column",
      }}
      className={classes.card}
    >
      {/* Progress bar header, absolutely positioned inside the panel */}
      {barVisible && (
        <Box
          //  sx={{
          //   position: "absolute",
          //   top: 8,
          //   left: 12,
          //   right: 12,
          //   zIndex: 3,
          //   pointerEvents: "none",
          //       }}
          sx={{ height: `${BAR_HEIGHT}px`, width: "100%", px: 1, pt: 0.5 }}
        >
          <Typography variant="body2" sx={{ color: "white", mb: 0.5 }}>
            {auditJobStatus === "starting"
              ? "Starting…"
              : `Generating report… ${Math.max(1, auditPercent ?? 1)}%`}
          </Typography>
          <LinearProgress
            // variant="determinate"
            //       value={Math.max(1, auditPercent ?? 1)}
            variant={
              auditJobStatus === "starting" ? "indeterminate" : "determinate"
            }
            value={
              auditJobStatus === "starting"
                ? undefined
                : Math.max(1, auditPercent ?? 1)
            }
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          width: "100%",
          height: `${BASE_HEIGHT}px`,
        }}
      >
        {/* Left scroll button */}
        {SelectedSection.length > 3 && (
          <IconButton
            onClick={() => scroll("left")}
            sx={{ flexShrink: 0, color: "white" }}
            className={classes.buttonHover}
          >
            <ChevronLeft />
          </IconButton>
        )}

        {/* Cards container */}
        <div
          ref={containerRef}
          style={{
            display: "flex",
            gap: 12,
            height: "100%",
            flex: 1,
            alignItems: "center",
            overflowX: "auto",
            overflowY: "hidden",
            scrollbarWidth: "none", // Firefox
            msOverflowStyle: "none", // IE
          }}
        >
          {SelectedSection.length ? (
            SelectedSection.map(
              (
                {
                  title,
                  uniqueKey,
                  llmName,
                  category,
                  market,
                  competitorName,
                  TabName,
                  persona
                },
                index
              ) => {
                const object = {
                  category,
                  llmName,
                  market,
                  persona,
                  competitorName,
                  TabName,
                };
                const filterOption = Object.entries(object).filter(
                  (ele) => ele[1]
                );

                return (
                  <Card
                    key={uniqueKey}
                    sx={{
                      height: "95%",
                      padding: "2px",
                      aspectRatio: 1.5,
                      position: "relative",
                      flexShrink: 0,
                      backgroundColor: "#E3F2FD",
                      borderRadius: 2,
                      borderLeft: "6px solid #007BFF",

                      "&:hover .delete-btn": { opacity: 1 },
                    }}
                  >
                    <CardContent
                      sx={{
                        textAlign: "start",
                        fontSize: ".9rem",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-start",
                        padding: "2px",
                      }}
                    >
                      <div>
                        <span title={title} className={classes.text}>
                          {title}
                        </span>
                      </div>
                      {filterOption.map((filter) => (
                        <div key={filter[0]}>
                          <span className={classes.sub_text} title={filter[1]}>
                            {filter[1]}
                          </span>
                        </div>
                      ))}
                      <div
                        style={{
                          justifyContent: "center",
                          fontWeight: "bold",
                          flex: 1,
                          alignItems: "flex-end",
                          display: "flex",
                          fontSize: ".8rem",
                        }}
                      >
                        {index + 1}
                      </div>
                    </CardContent>

                    <IconButton
                      className="delete-btn"
                      size="small"
                      onClick={() => handleDelete(uniqueKey)}
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        bgcolor: "error.main",
                        color: "white",
                        opacity: 0,
                        transition: "opacity 0.3s",
                        "&:hover": { bgcolor: "error.dark" },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Card>
                );
              }
            )
          ) : (
            <h3 style={{ color: "#3ad5cc" }}>
              Please select the widgets you want to include in the screenshot.
            </h3>
          )}
        </div>

        {SelectedSection.length > 3 && (
          <IconButton
            onClick={() => scroll("right")}
            sx={{ flexShrink: 0, color: "white" }}
            className={classes.buttonHover}
          >
            <ChevronRight />
          </IconButton>
        )}

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 5,
            marginLeft: 10,
          }}
        >
          <FormControlLabel
            control={<Checkbox checked={selectAll} onChange={handleCheck} />}
            label="Select All"
            style={{
              color: "white",
              fontWeight: "bold",
              textAlign: "center",
            }}
          />
          <MUIButton
            onClick={handleReportConfig}
            startIcon={<SettingsOutlined />}
            style={{
              cursor: "pointer",
            }}
            disabled={loading}
          >
            Report Configuration
          </MUIButton>

          <MUIButton
            onClick={handleDownload}
            loading={loading}
            disabled={barVisible}
            startIcon={<DownloadIcon />}
            style={{
              cursor: !SelectedSection.length ? "not-allowed" : "pointer",
            }}
          >
            Export Report
          </MUIButton>
          <MUIButton onClick={selectReportSection} startIcon={<CancelIcon />}>
            Close
          </MUIButton>
        </div>

        <PPTReportConfigModal
          open={showPPTConfigModal}
          onClose={handleCloseModal}
          onGenerate={handleGeneratePPTCombinations}
          brandName={entity}
          categories={category}
          llms={llm}
          markets={markets}
          personas={persona}
          dashboards={getDashboardList()}
          preselectedValues={getPreselectedValues()}
        />
      </Box>
    </Box>
  );
};

export default SelectedComponent;

function resolvePersonaForCombination({
  personaSetting,
  category,
  market,
  primaryCategory,
  primaryMarket,
  primaryPersona,
  personaFromCombination,
  title
}) {
  const applyAllCategory = !!personaSetting?.applyPersonaToAllCategory;
  const applyAllMarkets = !!personaSetting?.applyPersonaToAllMarkets;

  const SPECIAL_METRICS = [
    'Positive and Negative Attributes',
    ENTITY_LABELS.brandPillar,
    ENTITY_LABELS.productDimension,
    ENTITY_LABELS.purchaseCriteria
  ];

  if (SPECIAL_METRICS.includes(title)) {
    // Only check applyAllCategory, ignore applyAllMarkets
    if (applyAllCategory) {
      return personaFromCombination;
    }

    // applyAllCategory = false
    if (category === primaryCategory) {
      return personaFromCombination; // All personas for primary category
    } else {
      // Only primary persona for other categories
      if (personaFromCombination !== primaryPersona) {
        return null;
      }
      return personaFromCombination;
    }
  }

  // CASE 4: All personas everywhere
  if (applyAllCategory && applyAllMarkets) {
    return personaFromCombination;
  }

  // CASE 1: Both restricted
  if (!applyAllCategory && !applyAllMarkets) {
    if (
      (category !== primaryCategory && personaFromCombination !== primaryPersona) ||
      (market !== primaryMarket && personaFromCombination !== primaryPersona)
    ) {
      return null; 
    }
    return personaFromCombination;
  }

  // CASE 2: All markets, restricted category
  if (!applyAllCategory && applyAllMarkets) {
    if (category !== primaryCategory && personaFromCombination !== primaryPersona) {
      return null;
    }
    return personaFromCombination;
  }

  // CASE 3: All categories, restricted market
  if (applyAllCategory && !applyAllMarkets) {
    if (market !== primaryMarket && personaFromCombination !== primaryPersona) {
      return null;
    }
    return personaFromCombination;
  }

  return personaFromCombination;
}

function CreateCombination({
  llm,
  market,
  category,
  persona,
  primaryMarket,
  discoveryType,
  competitorsCategoryWise,
  entity,
  primaryLLM,
  primaryPersona,
  personaSetting
}) {
  let tabWise = {
    nev_brandoverview: {
      tabName:
        discoveryType === "brand" ? `${ENTITY_LABELS.brand} Overview` : `${ENTITY_LABELS.product} Overview`,
      tabId: "nev_brandoverview",
      uniqueKeyGeneration: ({ market, llm, category, persona, id }) => {
        let tabId = tabWise.nev_brandoverview.tabId;
        return `${tabId}_${id}_${llm ?? ""}_${category ?? ""}_${market ?? ""}_${persona ?? ""}`;
      },
      filterPush: ({ llm, market, category, persona }) => {
        const llmName = OVERVIEW_LLM_LABELS?.[llm] ?? "";

        return {
          llm: llm,
          category: category,
          market: market,
          persona,
          TabName: tabWise.nev_brandoverview.tabName,
          llmName,
          tab: tabWise.nev_brandoverview.tabId,
        };
      },

      component: {
        Brand_Summary: {
          Dependency: ["market", "persona"],
          id: "Brand_Summary",
          title:
            discoveryType === "brand" ? `${ENTITY_LABELS.brand} Summary` : `${ENTITY_LABELS.product} Summary`,

          uniqueKeyGenerationCom: ({ market, persona, id }) => {
            let tabId = tabWise.nev_brandoverview.tabId;
            return `${tabId}_${id}_${market}_${persona}`;
          },
          filterPushComponentWise: ({ llm, category, market, persona }) => {
            return {
              llm: null,
              category: null,
              market,
              persona
            };
          },
        },
        Sentiment_Drivers: {
          Dependency: ["llm", "category", "market", "persona"],
          id: "Sentiment_Drivers",
          title: "Sentiment Driver",
        },
        Attributes_Overview: {
          Dependency: ["category", "persona"],
          id: "Attributes_Overview",
          title: "Positive and Negative Attributes",
          uniqueKeyGenerationCom: ({ category, persona, id }) => {
            let tabId = tabWise.nev_brandoverview.tabId;
            return `${tabId}_${id}_${category}_${persona}`;
          },

          filterPushComponentWise: () => {
            return {
              llm: null,
              market: primaryMarket,
            };
          },
        },
        Sources_Overview: {
          Dependency: ["category", "market", "persona"],
          id: "Sources_Overview",
          title: "Sources Overview",
          uniqueKeyGenerationCom: ({ category, market, persona, id }) => {
            let tabId = tabWise.nev_brandoverview.tabId;
            return `${tabId}_${id}_${category}_${market}_${persona}`;
          },
          filterPushComponentWise: () => {
            return {
              llm: null,
            };
          },
        },
        FocusArea_Overview: {
          Dependency: ["category", "market", "persona"],
          id: "FocusArea_Overview",
          title: "Focus Area",
          uniqueKeyGenerationCom: ({ category, id, market, persona }) => {
            let tabId = tabWise.nev_brandoverview.tabId;
            return `${tabId}_${id}_${category}_${market}_${persona}`;
          },
          filterPushComponentWise: () => {
            return {
              llm: null,
            };
          },
        },
      },
    },

    nev_competitiveoverview: {
      tabName: "Competitive Overview",
      tabId: "nev_competitiveoverview",
      uniqueKeyGeneration: ({ llm, category, id }) => {
        let tabId = tabWise.nev_competitiveoverview.tabId;
        return `${tabId}_${id}_${llm ?? ""}_${category ?? ""}_${persona}`;
      },
      filterPush: ({ llm, category, persona, market }) => {
        const llmName = OVERVIEW_LLM_LABELS?.[llm] ?? "";

        return {
          llm: llm,
          category: category,
          persona,
          market,
          TabName: tabWise.nev_competitiveoverview.tabName,
          llmName,
          tab: tabWise.nev_competitiveoverview.tabId,
        };
      },
      component: {
        Competitor_Summary: {
          Dependency: ["category", "persona", "market"],
          id: "Competitor_Summary",
          title: "Competitor Summary",
          uniqueKeyGenerationCom: ({ category, persona, market, id }) => {
            let tabId = tabWise.nev_competitiveoverview.tabId;
            return `${tabId}_${id}_${category}_${market}_${persona}`;
          },
          filterPushComponentWise: ({ }) => {
            return {
              llm: null,
            };
          },
        },

        Sentiment_Drivers: {
          Dependency: ["llm", "category", "persona", "market"],
          specialCase: true,
          id: "Sentiment_Drivers",
          title: "Sentiment Drivers",
          uniqueKeyGenerationCom: ({ category, llm, id, competitor, persona, market }) => {
            let tabId = tabWise.nev_competitiveoverview.tabId;
            return `${tabId}_${id}_${llm}_${category}_${market}_${persona}_${competitor}`;
          },
          filterPushComponentWise: ({ competitor, competitorName }) => {
            return {
              competitor,
              competitorName,
            };
          },
        },

        Attributes_Overview: {
          Dependency: ["llm", "category", "persona"],
          id: "Attributes_Overview",
          title: "Positive and Negative Attributes",
          uniqueKeyGenerationCom: ({ category, persona, id }) => {
            let tabId = tabWise.nev_competitiveoverview.tabId;
            return `${tabId}_${id}_${llm ?? ""}_${category}_${persona}`;
          },
          filterPushComponentWise: () => {
            return {
              market: primaryMarket,
            };
          },
        },

        TaxonomyAndCoreAttributes: {
          Dependency: ["llm", "category", "persona"],
          id: "TaxonomyAndCoreAttributes",
          title:
            discoveryType === "brand" ? ENTITY_LABELS.brandPillar : ENTITY_LABELS.productDimension,
          uniqueKeyGenerationCom: ({ category, persona, id }) => {
            let tabId = tabWise.nev_competitiveoverview.tabId;
            return `${tabId}_${id}_${llm ?? ""}_${category}_${persona}`;
          },
          filterPushComponentWise: () => {
            return {
              market: primaryMarket,
            };
          },
        },

        purchase_criteria: {
          Dependency: ["llm", "category", "persona"],
          id: "purchase_criteria",
          title: ENTITY_LABELS.purchaseCriteria,
          uniqueKeyGenerationCom: ({ category, persona, id }) => {
            let tabId = tabWise.nev_competitiveoverview.tabId;
            return `${tabId}_${id}_${llm ?? ""}_${category}_${persona}`;
          },
          filterPushComponentWise: () => {
            return {
              market: primaryMarket,
            };
          },
        },
      },
    },

    nev_marketoverview: {
      tabName: "Market Overview",
      tabId: "nev_marketoverview",
      uniqueKeyGeneration: ({ market, llm, category, persona, id }) => {
        let tabId = tabWise.nev_marketoverview.tabId;
        return `${tabId}_${id}_${llm ?? ""}_${category ?? ""}_${market ?? ""}_${persona ?? ""}`;
      },
      filterPush: ({ llm, market, category, persona }) => {
        const llmName = OVERVIEW_LLM_LABELS[llm];

        return {
          llm: llm,
          category: category,
          market: market,
          persona,
          TabName: tabWise.nev_marketoverview.tabName,
          llmName,
          tab: tabWise.nev_marketoverview.tabId,
        };
      },

      component: {
        Brand_Summary: {
          Dependency: ["category", "persona"],
          id: "Market_Summary",
          title: "Market Summary",
          uniqueKeyGenerationCom: ({ category, persona, id }) => {
            let tabId = tabWise.nev_marketoverview.tabId;
            return `${tabId}_${id}_${category}_${persona}`;
          },
          filterPushComponentWise: ({ }) => {
            return {
              llm: null,
              market: null,
            };
          },
        },
        Sentiment_Drivers: {
          Dependency: ["llm", "category", "market", "persona"],
          id: "Sentiment_Drivers",
          title: "Sentiment Driver",
        },
      },
    },

    nev_personaoverview: {
      tabName: "Persona Overview",
      tabId: "nev_personaoverview",
      uniqueKeyGeneration: ({ market, llm, category, persona, id }) => {
        let tabId = tabWise.nev_personaoverview.tabId;
        return `${tabId}_${id}_${llm ?? ""}_${category ?? ""}_${market ?? ""}_${persona ?? ""}`;
      },
      filterPush: ({ llm, market, category, persona }) => {
        const llmName = OVERVIEW_LLM_LABELS[llm];

        return {
          llm: llm,
          category: category,
          market: market,
          persona: persona,
          TabName: tabWise.nev_personaoverview.tabName,
          llmName,
          tab: tabWise.nev_personaoverview.tabId,
        };
      },

      component: {
        Persona_Summary: {
          Dependency: ["category", "market"],
          id: "Persona_Summary",
          title: "Persona Summary",
          uniqueKeyGenerationCom: ({ category, market, id }) => {
            let tabId = tabWise.nev_personaoverview.tabId;
            return `${tabId}_${id}_${category}_${market}`;
          },
          filterPushComponentWise: ({ }) => {
            return {
              llm: null,
              persona: null,
            };
          },
        },
        Sentiment_Drivers: {
          Dependency: ["llm", "category", "market", "persona"],
          id: "Sentiment_Drivers",
          title: "Sentiment Driver",
        },
      },
    },

    // nev_conversation: {
    //   tabName: "Conversation",
    //   tabId: "nev_conversation",

    //   generateAll: ({ category, market, llm }) => {
    //     const result = {};

    //     const primaryCategory = category[0];
    //     const primaryMarket = market[0];
    //     const primaryLLM = llm[0];
    //     const CONVERSATION_TABLES = getConversationTables(discoveryType);
    //     category.map((selectedCategory) => {
    //       const tablesToGenerate =
    //         selectedCategory === primaryCategory
    //           ? CONVERSATION_TABLES // Primary → ALL
    //           : CONVERSATION_TABLES.filter(({ id }) =>
    //             id.startsWith("Category_") // Other → only Category_
    //           );

    //       tablesToGenerate.forEach(({ id, title }) => {
    //         const uniqueKey = `${tab[selectedCategory]}_${id}_${primaryLLM}_${primaryMarket}`;

    //         result[uniqueKey] = {
    //           llm: primaryLLM,
    //           category: selectedCategory,
    //           market: primaryMarket,

    //           TabName: "Conversation",
    //           primaryLLMName: OVERVIEW_LLM_LABELS[primaryLLM],

    //           id,
    //           title,

    //           tab: "nev_conversation",
    //           uniqueKey,
    //         };
    //       });
    //     })
    //     return result;
    //   },
    // },




    categoryTab: function ({ tabId, tabName }) {
      return {
        isCategoryTab: true,
        tabName: tabName,
        tabId: tabId,
        uniqueKeyGeneration: ({ llm, id }) => {
          return `${tabId}_${id}_${llm ?? ""}`;
        },
        filterPush: ({ llm }) => {
          const llmName = OVERVIEW_LLM_LABELS[llm];

          return {
            llm: llm,
            category: tabName,
            TabName: tabName,
            llmName,
            tab: tabId,
            market: primaryMarket,
            persona: primaryPersona,
            isCategoryTab: true
          };
        },
        component: {
          BrandInsights: {
            Dependency: ["llm"],
            categoryValue: tabName,
            id: `BrandInsights`,
            title: `${discoveryType === "brand" ? ENTITY_LABELS.brand : ENTITY_LABELS.product} Insights`,
          },
          BrandIndexScore: {
            Dependency: ["persona", "market"],
            id: `indexscore`,
            title: `${discoveryType === "brand" ? ENTITY_LABELS.brand : ENTITY_LABELS.product} Index Score`,
            categoryValue: tabName,
            uniqueKeyGenerationCom: ({ id, market = "", persona = "" }) => {
              return `${tabId}_${id}_${market}_${persona}`;
            },
            filterPushComponentWise: ({ market, llm, persona }) => {
              return {
                market,
                llm,
                persona,
                testLLM: primaryLLM
              };
            },
          },

          competitor: {
            Dependency: ["llm", "market", "persona"],
            id: `competitor`,
            title: `${discoveryType === "category" ? "Products" : "Competitor Analysis"}`,
            categoryValue: tabName,
            uniqueKeyGenerationCom: ({ llm = "", id, market = "", persona = "" }) => {
              return `${tabId}_${id}_${llm ?? ""}_${market}_${persona}`;
            },
            filterPushComponentWise: ({ market, llm, persona }) => {
              return {
                market,
                llm,
                persona
              };
            },
          },

          Reach_data: {
            Dependency: ["llm", "market", "persona"],
            specialCase: true,
            categoryValue: tabName,
            id: `Reach_data`,
            title: `${ENTITY_LABELS.rank} Data`,
            uniqueKeyGenerationCom: ({ llm = "", id, market = "", persona = "", competitor = 0 }) => {
              return `${tabId}_${id}_${llm ?? ""}_${market}_${persona}_${competitor}`;
            },
            filterPushComponentWise: ({ competitor, competitorName, llm, market, persona }) => {
              return {
                competitor,
                competitorName,
                market,
                llm,
                persona
              };
            },
          },

          sentiment_data: {
            Dependency: ["llm", "market", "persona"],
            specialCase: true,
            categoryValue: tabName,
            id: `sentiment_data`,
            title: "Sentiment Data",
            uniqueKeyGenerationCom: ({ llm = "", id, market = "", persona = "", competitor = 0 }) => {
              return `${tabId}_${id}_${llm ?? ""}_${market}_${persona}_${competitor}`;
            },
            filterPushComponentWise: ({ competitor, competitorName, llm, market, persona }) => {
              return {
                competitor,
                competitorName,
                market,
                llm,
                persona
              };
            },
          },
          sentiment_driver: {
            Dependency: ["llm", "market", "persona"],
            specialCase: true,
            categoryValue: tabName,
            id: `sentiment_driver`,
            title: "Sentiment Driver",
            uniqueKeyGenerationCom: ({ llm = "", id, market = "", persona = "", competitor = 0 }) => {
              return `${tabId}_${id}_${llm ?? ""}_${market}_${persona}_${competitor}`;
            },
            filterPushComponentWise: ({ competitor, competitorName, llm, market, persona }) => {
              return {
                competitor,
                competitorName,
                market,
                llm,
                persona
              };
            },
          },
          positiveNegativeAttributes: {
            Dependency: ["llm", "persona"],
            id: `positiveNegativeAttributes`,
            title: "Positive and Negative Attributes",
            categoryValue: tabName,
            uniqueKeyGenerationCom: ({ llm = "", id, persona = "" }) => {
              return `${tabId}_${id}_${llm ?? ""}_${persona}`;
            },
            filterPushComponentWise: ({ llm, persona }) => {
              return {
                llm,
                persona
              };
            },
          },

          Brand_Piller: {
            Dependency: ["llm", "persona"],
            id: `${discoveryType === "brand" ? "Brand_Piller" : "Product_Dimension"}`,
            title: `${discoveryType === "brand" ? ENTITY_LABELS.brandPillar : ENTITY_LABELS.productDimension}`,
            categoryValue: tabName,
            uniqueKeyGenerationCom: ({ llm = "", id, persona = "" }) => {
              return `${tabId}_${id}_${llm ?? ""}_${persona}`;
            },
            filterPushComponentWise: ({ llm, persona }) => {
              return {
                llm,
                persona
              };
            },
          },

          purchase_criteria: {
            Dependency: ["llm", "persona"],
            id: `purchase_criteria`,
            title: ENTITY_LABELS.purchaseCriteria,
            categoryValue: tabName,
            uniqueKeyGenerationCom: ({ llm = "", id, persona = "" }) => {
              return `${tabId}_${id}_${llm ?? ""}_${persona}`;
            },
            filterPushComponentWise: ({ llm, persona }) => {
              return {
                llm,
                persona
              };
            },
          },

          sources: {
            Dependency: ["llm", "market", "persona"],
            id: `sources`,
            title: "Sources",
            categoryValue: tabName,
            uniqueKeyGenerationCom: ({ llm = "", id, market = "", persona = "" }) => {
              return `${tabId}_${id}_${llm ?? ""}_${market}_${persona}`;
            },
            filterPushComponentWise: ({ market, persona }) => {
              return {
                market: market,
                persona
              };
            },
          },
          realtimeActions: {
            Dependency: ["llm", "market", "persona"],
            id: `realtimeActions`,
            title: "Focus Area",
            categoryValue: tabName,
            uniqueKeyGenerationCom: ({ llm = "", id, market = "", persona = "" }) => {
              return `${tabId}_${id}_${llm}_${market}_${persona}`;
            },
            filterPushComponentWise: ({ market, persona }) => {
              return {
                market: market,
                persona
              };
            },
          },
        },
      };
    },
  };

let TabWiseCompoent = Object.entries(tabName).map(([tabId, tabName]) => {
  const isOverviewTab =
    (tabId === "nev_marketoverview" && market.length <= 1) ||
    (tabId === "nev_personaoverview" && persona.length <= 1);

  if (isOverviewTab) return null;
  if (tabWise[tabId]) {
    return tabWise[tabId];
  }

  return tabWise.categoryTab({ tabId, tabName });
});

let filter = {
  llm,
  category,
  market,
  persona
};

let combination = {};

TabWiseCompoent.forEach((curr) => {
  if (!curr) {
    return;
  }
  let { component, uniqueKeyGeneration, filterPush, isCategoryTab, tabId } = curr;
  if (!component) return;

  let localCombination = {};

  // Get all unique dependencies across all components in this dashboard
  const allDependencies = [...new Set(
    Object.values(component).flatMap(c => c.Dependency)
  )];
  
  // Build base combinations
  const baseCombinationData = allDependencies.map((dep) => filter[dep]);
  const baseCombinations = generateCombination(baseCombinationData, allDependencies);

  // Unified logic: combination-first → metrics-second
  baseCombinations.forEach((baseComb) => {
    Object.values(component).forEach((com) => {
      let {
        id,
        title,
        Dependency,
        uniqueKeyGenerationCom,
        filterPushComponentWise,
        specialCase,
        categoryValue
      } = com;

      // Extract only relevant dependencies for this component
      const relevantComb = {};
      Dependency.forEach((dep) => {
        if (baseComb[dep] !== undefined) {
          relevantComb[dep] = baseComb[dep];
        }
      });

      // Validate persona if present
      if (relevantComb.persona) {
        const resolvedPersona = resolvePersonaForCombination({
          personaSetting,
          category: isCategoryTab ? categoryValue : (relevantComb.category ?? category[0]),
          market: relevantComb.market ?? primaryMarket,
          primaryCategory: category?.[0],
          primaryMarket,
          primaryPersona,
          personaFromCombination: relevantComb.persona,
          title,
        });
        if (resolvedPersona === null) return; // skip invalid combo
        relevantComb.persona = resolvedPersona;
      }

      if (!specialCase) {
        let uniqueKey = uniqueKeyGenerationCom
          ? uniqueKeyGenerationCom({ ...relevantComb, id })
          : uniqueKeyGeneration({ ...relevantComb, id });

        localCombination[uniqueKey] = {
          ...(filterPush?.(relevantComb) ?? {}),
          ...(filterPushComponentWise?.(relevantComb) ?? {}),
          id,
          title,
          uniqueKey,
        };
      } else {
        // Special case with competitors
        let categoryForComp = isCategoryTab ? categoryValue : (relevantComb.category ?? category[0]);
        let competitorListForCategory = [...competitorsCategoryWise?.[categoryForComp]] ?? [];
        competitorListForCategory.unshift({ name: entity });

        competitorListForCategory.forEach((competitor, index) => {
          const combWithCompetitor = {
            ...relevantComb,
            competitorName: competitor?.name ?? "",
            competitor: index,
          };

          // Validate persona again for competitor combinations if present
          if (combWithCompetitor.persona && Dependency.includes("persona")) {
            const resolvedPersona = resolvePersonaForCombination({
              personaSetting,
              category: categoryForComp,
              market: combWithCompetitor.market ?? primaryMarket,
              primaryCategory: category?.[0],
              primaryMarket,
              primaryPersona,
              personaFromCombination: combWithCompetitor.persona,
              title,
            });
            if (resolvedPersona === null) return;
            combWithCompetitor.persona = resolvedPersona;
          }

          let uniqueKey = uniqueKeyGenerationCom
            ? uniqueKeyGenerationCom({ ...combWithCompetitor, id })
            : uniqueKeyGeneration({ ...combWithCompetitor, id });

          localCombination[uniqueKey] = {
            ...(filterPush?.(combWithCompetitor) ?? {}),
            ...(filterPushComponentWise?.(combWithCompetitor) ?? {}),
            id,
            title,
            uniqueKey,
          };
        });
      }
    });
  });

  // Sort only for category tabs
  // if (isCategoryTab) {
  //   const marketOrder = market;
  //   const llmOrder = llm;
  //   const personaOrder = persona;

  //   const entries = Object.entries(localCombination);

  //   const sortedEntries = entries.sort(([, a], [, b]) => {
  //     const personaCompare =
  //       personaOrder.indexOf(a.persona) - personaOrder.indexOf(b.persona);
  //     if (personaCompare !== 0) return personaCompare;

  //     const marketCompare =
  //       marketOrder.indexOf(a.market) - marketOrder.indexOf(b.market);
  //     if (marketCompare !== 0) return marketCompare;

  //     return llmOrder.indexOf(a?.llm ?? (a.testLLM)) - llmOrder.indexOf(b?.llm ?? (b?.testLLM));
  //   });

  //   localCombination = Object.fromEntries(sortedEntries);
  // }

  combination = { ...combination, ...localCombination };
});

return combination;
}
